// ---------------------------------------------------------------------------
// seedFormStateFromTemplate — maps a saved EvaluationTemplate (as returned
// by the GET endpoint) into the local-state shape the form drives its
// inputs from.
//
// Pure function; called from `useState` initializers in
// EvaluationTemplateForm so view/edit mounts pre-populate cleanly with the
// saved values instead of an empty form.
//
// Agent details (model, prompt, etc.) aren't on the template — they live on
// the Agent resource — so seeded agents have empty internal fields. The
// form locks those inputs in edit mode to avoid clobbering server data we
// don't have a snapshot of.
// ---------------------------------------------------------------------------

import { extractBindings } from './bindings.utils.js';
import {
  DATA_LAKE_SOURCE_TYPE,
  type AgentDraft,
  type EvaluationTemplateWithConfig,
  type ParallelismChoice,
  type SeededFormState,
  type TaskDraft,
  type VariableRow,
  type VariableType,
} from './EvaluationTemplateForm.types.js';
import {
  formatVariableDefault,
  makeTask,
  uid,
} from './EvaluationTemplateForm.utils.js';

export function seedFormStateFromTemplate(
  template: EvaluationTemplateWithConfig,
): SeededFormState {
  const tasks = template.tasks ?? [];

  const buildAgent = (
    name: string | undefined,
    version: number | undefined,
  ): AgentDraft => ({
    id: uid('a'),
    // Saved templates reference an agent that already exists in the
    // workspace store; we record the name only.
    name: name ?? '',
    version,
  });

  // Single-run only — seed the first task (saved single-run templates have
  // exactly one). Any extra tasks from legacy compare templates are ignored.
  const firstTask = tasks[0];
  const taskDrafts: TaskDraft[] = firstTask
    ? [
        {
          kind: 'single',
          id: uid('t'),
          name: firstTask.name ?? 'default',
          agent: buildAgent(firstTask.agent?.name, firstTask.agent?.version),
          graderRows: (firstTask.graders ?? []).map((g) => ({
            id: uid('g'),
            name: g.name ?? '',
            version: g.version,
          })),
        },
      ]
    : [makeTask('default')];

  const variables: VariableRow[] = Object.entries(template.variables ?? {}).map(
    ([name, decl]) => ({
      id: uid('v'),
      name,
      type: (decl.type as VariableType | undefined) ?? 'string',
      defaultValue: formatVariableDefault(decl.default),
      description: decl.description ?? '',
    }),
  );

  const querySetItems = template.querySets?.[0]?.items ?? [];
  const firstDataSet = template.dataSets?.[0];
  const dataSetItems = firstDataSet?.items ?? [];
  const dataSetSource =
    firstDataSet?.source?.sourceType === DATA_LAKE_SOURCE_TYPE
      ? firstDataSet.source
      : null;

  return {
    jobName: template.name,
    jobDisplayName: template.displayName ?? '',
    jobDescription: template.description ?? '',
    taskDrafts,
    variables,
    // Recover per-variable bindings the template stored in its `with:` clauses.
    bindings: extractBindings(firstTask),
    // Server can return `workers: null` (not just undefined) when the
    // field was never set — guard both, otherwise the input would display
    // the literal string "null".
    workersText:
      typeof template.concurrency?.workers === 'number'
        ? String(template.concurrency.workers)
        : '',
    parallelism:
      (template.concurrency?.parallelism as ParallelismChoice | undefined) ??
      'task',
    // Saved templates have items inline — we don't yet support reference
    // shapes for query sets, so always show the 'inline' tab.
    querySetMode: 'inline',
    queryInlineText:
      querySetItems.length > 0 ? JSON.stringify(querySetItems, null, 2) : '',
    dataSetEnabled: (template.dataSets?.length ?? 0) > 0,
    // Detect whether the saved data set is a Data Lake reference or
    // inline items. The picker takes over in the former case.
    dataSetMode: dataSetSource !== null ? 'existing' : 'inline',
    dataInlineText:
      dataSetItems.length > 0 ? JSON.stringify(dataSetItems, null, 2) : '',
    dataSetExistingId: dataSetSource?.dataSetId ?? null,
    dataSetExistingVersion: dataSetSource?.version ?? null,
    // Preserve any saved file selectors verbatim so editing never clobbers
    // a selector authored via API/CLI (e.g. a custom name or glob).
    dataSetFileSelector: dataSetSource?.fileSelector ?? [],
  };
}
